// AlertContext.js
import React, { createContext, useState, useContext } from "react";
import { Loader } from "../Components/Common/Loader";

// Create Context
const AlertContext = createContext();

// Create Provider component
export const AlertProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [onConfirm, setOnConfirm] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const showAlert = async (message, onConfirm) => {
    setMessage(message);
    setOnConfirm(() => onConfirm);
    setIsOpen(true);
  };

  const closeAlert = () => {
    setIsOpen(false);
    setOnConfirm(null);
  };

  return (
    <AlertContext.Provider value={{ showAlert, closeAlert, setIsLoading }}>
      {children}
      <div
        id="popup-modal"
        tabIndex="-1"
        className={`fixed inset-0 flex items-center justify-center z-50 transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        {/* Modal Background */}
        <div
          className={`fixed inset-0 bg-gray-900 bg-opacity-50 transition-opacity duration-300 ${
            isOpen ? "opacity-50" : "opacity-0"
          }`}
          onClick={closeAlert}
        ></div>
        <div className="relative w-full max-w-md mx-auto p-6 bg-white rounded-lg shadow-xl dark:bg-gray-800">
          {/* Close Button */}
          <button
            type="button"
            className="absolute top-2 right-2 text-gray-500 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white"
            onClick={closeAlert}
          >
            <svg
              className="w-5 h-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <span className="sr-only">Close modal</span>
          </button>

          <div className="text-center space-y-4">
            {/* Icon */}
            <svg
              className="mx-auto text-gray-400 w-16 h-16 dark:text-gray-200"
              aria-hidden="true"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 20 20"
            >
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M10 11V6m0 8h.01M19 10a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
              />
            </svg>

            <h3 className="text-lg font-medium text-gray-800 dark:text-gray-200">
              {message}
            </h3>

            <div className="flex justify-center space-x-4">
              <button
                type="button"
                disabled={isLoading}
                className="text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-4 focus:ring-red-300 dark:focus:ring-red-700 font-medium rounded-lg text-sm px-6 py-2.5"
                onClick={async () => {
                  if (onConfirm) await onConfirm();
                  closeAlert(); // Close modal after async task
                }}
              >
                {isLoading ? <Loader /> : `Yes, I'm sure`}
              </button>

              <button
                type="button"
                className="text-gray-800 bg-gray-100 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 dark:focus:ring-gray-600 font-medium rounded-lg text-sm px-6 py-2.5"
                onClick={closeAlert}
              >
                No, cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </AlertContext.Provider>
  );
};

// Custom hook to use alert context
export const useAlert = () => {
  return useContext(AlertContext);
};
